import classNames from 'classnames'
import React, { useState } from 'react'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { Breakpoints } from '../utilities/breakpoints'
import { isNullOrWhiteSpace, sanitizeHtmlAndReplaceNewlines } from '../utilities/string'
import { Grid } from './Grid'
import { Image, ImageProps } from './Image'
import { Parallax } from './Parallax'

export interface ParallaxHeroProps {
  backgroundImageProps?: ImageProps
  blueOverlay?: boolean
  detailsText?: string
  detailsTitle?: string
  mainText?: string
  parentTitle?: string
  title: string
  variant?: 'default' | 'business-line'
}

export const ParallaxHero = ({
  backgroundImageProps,
  blueOverlay,
  detailsText,
  detailsTitle,
  mainText,
  parentTitle,
  title,
  variant = 'default',
}: ParallaxHeroProps): JSX.Element => {
  const [aboveMobile, setAboveMobile] = useState<boolean>(false)
  useBreakpoint(Breakpoints.tb, setAboveMobile)

  const isCompact = isNullOrWhiteSpace(mainText)
  const imageHeight = isCompact ? 'h-480' : 'h-[37.5rem]'

  const compactPadding = classNames({
    'tb:pb-90': variant === 'business-line',
    'tb:pb-30 sm:pb-60 xl:pb-90': variant !== 'business-line',
  })

  const compactHeight = backgroundImageProps
    ? classNames({
        'tb:h-[37.5rem] sm:h-[41.25rem] lg:h-[43.125rem] xl:h-[61.875rem] xxl:h-[84.375rem]':
          variant === 'business-line',
        [classNames(
          'tb:min-h-[24.375rem]',
          'sm:min-h-[34.375rem]',
          'md:min-h-[41.25rem]',
          'lg:min-h-[46.875rem]',
          'xl:min-h-[61.875rem]',
          'xxl:min-h-[84.375rem]'
        )]: variant !== 'business-line',
      })
    : undefined

  // Make parallax hero slightly oversized on y-axis to allow an
  // offset from the top of the page of up to 200px.
  // The Y position will be rendered closer to the top in the
  // parallax background due to perspective.
  const parallaxHeight =
    variant === 'business-line'
      ? 'tb:h-[40rem] sm:h-[43.75rem] lg:h-[46.875rem] xl:h-[65.625rem] xxl:h-[87.5rem]'
      : 'tb:h-auto'

  const heroAreaStyle = isCompact
    ? classNames(
        'hidden tb:grid',
        'tb:grid-rows-parallax-hero-compact tb:pt-45',
        'xl:pt-60',
        'xxl:pt-120',
        compactHeight,
        compactPadding
      )
    : classNames(
        'bg-senary-darkest min-h-480 py-45',
        'tb:min-h-[58.75rem] tb:grid-rows-parallax-hero tb:bg-transparent tb:pt-45 tb:pb-60',
        'sm:min-h-[71.875rem]',
        'md:min-h-[86.25rem]',
        'lg:min-h-[93.75rem]',
        'xl:min-h-[128.4375rem] xl:pt-60',
        'xxl:min-h-[172.5rem] xxl:py-90'
      )

  // don't scale hero image when docked at top of screen on business line page (all above mobile)
  const heroDockedAtTopOfPage = variant === 'business-line' && aboveMobile
  const scaleToFit = !heroDockedAtTopOfPage
  const longDetailText = !isNullOrWhiteSpace(detailsText) && detailsText.length >= 80
  const longDetailInfo =
    (!isNullOrWhiteSpace(detailsTitle) && detailsTitle.length >= 40) || longDetailText

  return (
    <div
      className={classNames({
        'tb:text-neutral': backgroundImageProps,
        'tb:text-secondary-darker': !backgroundImageProps,
      })}
    >
      {(!isNullOrWhiteSpace(parentTitle) ||
        !isNullOrWhiteSpace(title) ||
        !isNullOrWhiteSpace(detailsTitle) ||
        !isNullOrWhiteSpace(detailsText)) && (
        <Grid
          additionalClassName="
            relative bg-site-background text-secondary-darker
            tb:hidden
          "
        >
          <div
            className={classNames('relative col-span-full mr-30 pt-30 pb-45', {
              'min-h-330': variant !== 'business-line' ? '' : 'min-h-330',
            })}
          >
            {!isNullOrWhiteSpace(parentTitle) && (
              <small className="mb-15 font-hero-subtitle text-hero-subtitle">
                {parentTitle}
              </small>
            )}

            <h1 className="font-display text-display-500 mb-30">{title}</h1>

            {!isNullOrWhiteSpace(detailsTitle) && (
              <h2
                className={classNames('mb-15', {
                  'font-hero-body text-hero-body': variant === 'business-line',
                  'font-hero-title text-hero-title': variant !== 'business-line',
                })}
              >
                {detailsTitle}
              </h2>
            )}

            {!isNullOrWhiteSpace(detailsText) && (
              <p
                className={classNames({
                  'font-hero-body text-hero-body ': !longDetailText,
                  'font-hero-body text-hero-body-sm': longDetailText,
                })}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlAndReplaceNewlines(detailsText),
                }}
              />
            )}
          </div>
        </Grid>
      )}

      {backgroundImageProps && (
        <Parallax
          additionalClassName={classNames(imageHeight, 'tb:absolute', parallaxHeight)}
          id="parallax-hero"
          scaleToFit={scaleToFit}
        >
          <Image
            className={classNames('w-full', imageHeight, {
              'tb:h-full': variant === 'business-line',
              'tb:h-auto': variant !== 'business-line',
            })}
            imgClassName="w-full h-full object-cover"
            {...backgroundImageProps}
            caption=""
            copyright=""
          />

          {blueOverlay && (
            <div className="absolute inset-0 col-none blend-multiply bg-senary-lighter"></div>
          )}
        </Parallax>
      )}

      <Grid additionalClassName={heroAreaStyle}>
        <div
          className={classNames(
            'hidden',
            {
              'tb:flex tb:flex-col tb:col-span-full tb:mx-120':
                variant === 'business-line',
              'tb:block tb:col-start-1 tb:col-span-2 tb:ml-30':
                variant !== 'business-line',
            },
            'sm:col-start-2 sm:col-span-3 sm:mx-0',
            'md:col-start-1 md:col-span-3',
            'lg:col-start-2 lg:col-span-3',
            'xxl:col-start-3 xxl:col-span-4'
          )}
        >
          {!isNullOrWhiteSpace(parentTitle) && (
            <small
              className={classNames('font-hero-subtitle text-hero-subtitle', {
                'mt-auto mb-10 md:mb-30': variant === 'business-line',
                'mb-15': variant !== 'business-line',
              })}
            >
              {parentTitle}
            </small>
          )}

          {/* Add Display typography here */}
          <h1 className="font-display text-display-500">{title}</h1>
        </div>

        {!isNullOrWhiteSpace(mainText) && (
          <div
            className={classNames(
              'col-span-full flex justify-center items-center -mx-1',
              'sm:mx-120',
              'md:mx-0',
              'lg:col-start-2 lg:col-span-6',
              'xl:col-start-3 xl:col-span-6',
              'xxl:col-start-4 xxl:col-span-8'
            )}
          >
            <p
              className={classNames(
                'text-center text-neutral font-t-light text-t-600 leading-[3.75rem]',
                'tb:text-t-800',
                'xxl:text-t-900 xxl:leading-[6.25rem]'
              )}
            >
              {mainText}
            </p>
          </div>
        )}

        <div
          className={classNames(
            'hidden tb:block',
            {
              'pt-15 tb:col-span-full tb:mx-120 md:pt-20 md:-mr-90':
                variant === 'business-line',
              'pt-30 tb:col-start-1 tb:ml-30 tb:col-span-3': variant !== 'business-line',
            },
            'sm:col-start-2 sm:col-span-3 sm:mx-0',
            'md:col-start-1 md:col-span-3',
            'lg:col-start-2',
            'xxl:col-start-3',
            { 'lg:col-span-3': !longDetailInfo },
            { 'xxl:col-span-4': !longDetailInfo },
            { 'lg:col-span-4': longDetailInfo },
            { 'xxl:col-span-6': longDetailInfo }
          )}
        >
          {!isNullOrWhiteSpace(detailsTitle) && (
            <h2
              className={classNames(
                'mb-15 font-hero-title text-hero-title',
                'lg:mb-30',
                'xxl:mb-75'
              )}
            >
              {detailsTitle}
            </h2>
          )}

          {!isNullOrWhiteSpace(detailsText) && (
            <p
              className={classNames('xxl:-mr-30', {
                'font-hero-body text-hero-body ': !longDetailText,
                'font-hero-body text-hero-body-sm': longDetailText,
              })}
            >
              {detailsText}
            </p>
          )}
        </div>
      </Grid>
    </div>
  )
}
